import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { slsClient } from 'clients/slsClient'
import { medicareApiClient, MedicareApiHeaderNames } from 'clients/medicareApiClient'
import { determineCurrentLanguageFromUrl } from 'utils/language'
import { BeneficaryLoadErrors, User } from 'models/User'
import { LanguagePreference } from 'models/LanguagePreference'
import { Logger } from 'utils/logger'
import { clearMedicareMyCookies, cookieInterfaces, logoutFromOifIfLoggedIn } from 'utils/cookies'
import { BeneficiaryModelOptions } from '@cms-max/medicare-api-sdk'

export type IPublicBeneficiaryResponse = Pick<User, 'firstName'>

/**
 * @deprecated Being phased out with feature flag use-only-sls-jwt-for-auth-by-default
 */
export const fetchReqTokenAndUser = createAsyncThunk(
  'users/fetchReqTokenAndUser',
  async (_: undefined, { dispatch, rejectWithValue }) => {
    try {
      const currentUrl = window.location.href.toLowerCase()
      const currentLanguage = determineCurrentLanguageFromUrl(currentUrl)
      dispatch(setLanguage({ languagePreference: currentLanguage }))

      const { req_token: slsRequestToken } = await slsClient.getReqToken(currentLanguage)

      if (slsRequestToken) {
        // Get cookie for max session
        await medicareApiClient.asyncGetBeneficiaryByReqToken(slsRequestToken)
        // const getBeneDataFull:any = await medicareApiClient.asyncGetBeneficiaryFullData()
        const bene = await medicareApiClient.asyncGetBeneficiaryFullData()
        // const getBeneDataFull = await medicareApiClient.asyncGetBeneficiaryFullDataWithAccountSettings() // TODO - this needs change in production b/c email calls are expensive from BIC and take along time to load in
        return bene
      } else {
        throw Error('no slsRequestToken, see fetchReqTokenAndUser thunk')
      }
    } catch (e: unknown) {
      Logger.error(e)
      return rejectWithValue(undefined)
    }
  }
)

/**
 * Use to fetch user by calling the api /beneficiary endpoint
 */
export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async (_: undefined, { dispatch, rejectWithValue }) => {
    try {
      // I don't think we should really be fetching langauge here, but this is how it's worked the entire app lifetime.
      const currentUrl = window.location.href.toLowerCase()
      const currentLanguage = determineCurrentLanguageFromUrl(currentUrl)
      dispatch(setLanguage({ languagePreference: currentLanguage }))

      const verifyResponse = await medicareApiClient.asyncVerify()
      const { authenticated } = verifyResponse
      if (!authenticated) {
        return rejectWithValue(undefined)
      }

      const beneficiaryResponse = await medicareApiClient.beneficiaryApi.getBeneficiary()
      const sessionId = beneficiaryResponse.headers[MedicareApiHeaderNames.MaxSession]
      const user = beneficiaryResponse.data
      // TODO in a future ticket: Validate the beneficiary objects looks like it's expected
      return {
        sessionId,
        user
      }
    } catch (error) {
      Logger.error(error)
      return rejectWithValue(undefined)
    }
  }
)

export const fetchFullUser = createAsyncThunk(
  'users/fetchFullUser',
  async (fields: BeneficiaryModelOptions[], { dispatch }) => {
    const response = await medicareApiClient.asyncGetBeneficiaryFullData(fields)
    if (response) {
      dispatch(setUser(response))

      if (response?.user._errors && Object.keys(response?.user._errors).length) {
        dispatch(setErrors(response.user._errors))
      }
    } else {
      throw new Error('Full bene response is undefined')
    }
  }
)

interface IUserReducer {
  user?: Partial<User> & { firstName: string }
  sessionId?: string
  currentLanguagePreference: LanguagePreference
  status: 'finished' | 'error' | 'loading' | 'idle'
  errors?: BeneficaryLoadErrors
  itemsLastUpdated: {
    [key: string]: string
  }
}

type ISetUserAction = {
  // languagePreference is not on bene object from API, but perhaps might be used on FE
  user: User & {
    languagePreference?: LanguagePreference
  },
  sessionId: string
}
type ISetLanguagePreferenceAction = {
  languagePreference: LanguagePreference
}
type UpdateUserAction = Partial<User>
interface IUserGetter {
  users: IUserReducer
}

const initialState: IUserReducer = {
  user: undefined,
  currentLanguagePreference: 'en',
  status: 'idle',
  itemsLastUpdated: {}
}

// Exported for testing
export const utils = {
  // Using non-arrow function so we can spy on in jest tests
  handleSessionChange: function (newSessionId: string) {
    logoutFromOifIfLoggedIn()
    clearMedicareMyCookies()
    cookieInterfaces.session.set(newSessionId)
  }
}

export const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUser: (state: IUserReducer, { payload }: PayloadAction<ISetUserAction>) => {
      state.user = payload.user
      if (state.sessionId !== payload.sessionId) {
        if (cookieInterfaces.session.get() !== payload.sessionId) {
          utils.handleSessionChange(payload.sessionId)
        }
        state.sessionId = payload.sessionId
      }
    },
    updateUser: (state, action: PayloadAction<UpdateUserAction>) => {
      if (!state.user) {
        Logger.error('Attempted to update user, but none was set in the redux state')
        return
      }
      state.user = {
        ...state.user,
        ...action.payload
      }
    },
    clearUser: (state: IUserReducer) => {
      state.user = initialState.user
    },
    setLanguage: (state: IUserReducer, action: PayloadAction<ISetLanguagePreferenceAction>) => {
      state.currentLanguagePreference = action.payload.languagePreference
    },
    setErrors: (state: IUserReducer, action: PayloadAction<BeneficaryLoadErrors>) => {
      state.errors = action.payload
    },
    setLastUpdated: (state: IUserReducer, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload
      state.itemsLastUpdated = {
        ...state.itemsLastUpdated,
        [key]: (new Date()).toISOString()
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // pending case gets executed at the start of the async thunk
      .addCase(fetchReqTokenAndUser.pending, (state: IUserReducer) => {
        state.status = 'loading'
        state.user = {
          firstName: '...'
        }
      })
      // fulfilled case gets executed at the end of the async thunk
      // payload (2nd argument) is an optional return value in the async thunk
      .addCase(fetchReqTokenAndUser.fulfilled, (state: IUserReducer, { payload }) => {
        state.status = 'finished'
        // Payload should always be defined, but we could get undefined back if we receive an error back
        if (payload) {
          state.user = {
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            middleName: payload.user.middleName,
            railroadRetirementBoardBeneficiary: payload.user.railroadRetirementBoardBeneficiary,
            mailingAddress: payload.user.mailingAddress,
            email: payload.user.email,
            eHandbookPreferences: payload.user.eHandbookPreferences,
            eMsnPreferences: payload.user.eMsnPreferences,
            isMedicareAdvantage: payload.user.isMedicareAdvantage,
            username: payload.user.username,
            lastCardRequestDateMs: payload.user.lastCardRequestDateMs,
            mfa: payload.user.mfa
          }
          if (state.sessionId !== payload.sessionId) {
            if (cookieInterfaces.session.get() !== payload.sessionId) {
              utils.handleSessionChange(payload.sessionId)
            }
            state.sessionId = payload.sessionId
          }
        }
      })
      // rejected case gets executed if any errors are thrown
      .addCase(fetchUser.rejected, (state: IUserReducer) => {
        state.status = 'error'
        state.user = initialState.user
      })
      .addCase(fetchUser.pending, (state: IUserReducer) => {
        state.status = 'loading'
        state.user = {
          firstName: '...'
        }
      })
      // fulfilled case gets executed at the end of the async thunk
      // payload (2nd argument) is an optional return value in the async thunk
      .addCase(fetchUser.fulfilled, (state: IUserReducer, { payload }) => {
        state.status = 'finished'
        // Payload should always be defined, but we could get undefined back if we receive an error back
        if (payload) {
          state.user = {
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            middleName: payload.user.middleName,
            railroadRetirementBoardBeneficiary: payload.user.railroadRetirementBoardBeneficiary,
            mailingAddress: payload.user.mailingAddress,
            email: payload.user.email,
            eHandbookPreferences: payload.user.eHandbookPreferences,
            eMsnPreferences: payload.user.eMsnPreferences,
            isMedicareAdvantage: payload.user.isMedicareAdvantage,
            isImmunosuppressiveDrugCoverage: payload.user.isImmunosuppressiveDrugCoverage,
            username: payload.user.username,
            lastCardRequestDateMs: payload.user.lastCardRequestDateMs,
            mfa: payload.user.mfa
          }
          if (state.sessionId !== payload.sessionId) {
            if (cookieInterfaces.session.get() !== payload.sessionId) {
              utils.handleSessionChange(payload.sessionId)
            }
            state.sessionId = payload.sessionId
          }
        }
      })
      // rejected case gets executed if any errors are thrown
      .addCase(fetchReqTokenAndUser.rejected, (state: IUserReducer) => {
        state.status = 'error'
        state.user = initialState.user
      })
  }
})

export const {
  // Users
  setUser,
  updateUser,
  clearUser,
  setLanguage,
  setErrors,
  setLastUpdated
} = userSlice.actions

// GETTERS
export const getUserStatus = (state: IUserGetter) => state.users.status
/**
 * This will return true before all user data is retrieved. To wait until all user data is
 * retrieved use getUserStatus() and wait for it to be 'finished' or 'error'
 *
 * @returns if the user is logged in
 */
export const getUserLoggedInState = (state: IUserGetter) => Boolean(state.users.user)
export const getSessionId = (state: IUserGetter) => state.users.sessionId
export const getLanguage = (state: IUserGetter) => state.users.currentLanguagePreference
export const getUserData = (state: IUserGetter) => state.users
export const getUserMfaOptionsWithCounts = (state: IUserGetter) => {
  const mfaTypes = {
    email: 0,
    sms: 0,
    voice: 0
  }

  // Return a blank array if the user has no mfa, this makes truthy, falsy checks easier
  if (!state.users.user?.mfa) {
    return []
  }
  return state.users.user?.mfa?.configuredOptions.map((mfaOption) => ({
    ...mfaOption,
    // Append a count variation with the 'numbering' of the option
    count: ++mfaTypes[mfaOption.type]
  }))
}
export const getUser = (state: IUserGetter) => getUserData(state).user
export const getEMsnPreferences = (state: IUserGetter) => getUser(state)?.eMsnPreferences
export const getMedicareAdvantageStatus = (state: IUserGetter) => getUser(state)?.isMedicareAdvantage
export const getElmoOffline = (state: IUserGetter) => state.users.errors?.eHandbookPreferences === 'ELMO offline'
export const getErrors = (states: IUserGetter) => states.users.errors
export const getFormattedAddress = (reducerState: IUserGetter) => {
  if (!reducerState.users.user?.mailingAddress) {
    return ''
  }

  const { address1, city, state, zip } = reducerState.users.user.mailingAddress
  const formattedAddress = address1 ? `${address1} ` : ''
  const shouldInsertCommaAfterCity = Boolean(state)
  const formattedCity = city ? `${city}${shouldInsertCommaAfterCity ? ', ' : ' '}` : ''
  const formattedState = state ? `${state} ` : ''
  const formattedZip = zip ?? ''
  return `${formattedAddress}${formattedCity}${formattedState}${formattedZip}`
}

export default userSlice.reducer
